import {
  axiosInstance,
  ColDef,
  ColumnApi,
  ColumnState,
  ExportFormatEnum,
  ExportType,
  GridApi,
} from '@fides/fides-react-components';
import { GetRowNodeIdFunc, IRowNode } from '@ag-grid-community/core';
import { endpoints } from '../consts';
import { ColumnDefExport, DataExport, FiltersParams } from '../interfaces';
import { CreditTransferOrder, ExportSettings } from '../models';
import { CreditTransferReportType } from '../enums';
import { JsonObject } from '../types';
import { DelimiterType } from '../enums/DelimiterType';
import { mapSortModel } from '../helpers';
import { mapFilterModel } from '../../components/grid/hooks/useDataSource';

type Params = Record<string, any>;

type PersistedGridColumn = {
  colId?: string;
  hide?: boolean;
};

type PortalGridConfigResponse = {
  gridId: string;
  gridConfig?: string | null;
  filterModel?: string | null;
  pagination?: number;
};

type SnackbarVariant = 'info' | 'error' | 'default' | 'success' | 'warning';

export const getFileBody = (
  dataExport: DataExport,
  exportSettings: ExportSettings,
  selectedExportFormatFromDialog: ExportType
): DataExport => {
  const exportData: DataExport = { ...dataExport };

  exportData.delimiter = exportSettings?.delimiter
    ? DelimiterType[exportSettings?.delimiter]
    : DelimiterType.SETTINGS_EXPORTDELIMITER_OPTION_COMMA;

  const selectedExportFormatFromSettings: ExportType =
    exportSettings?.exportFormat === ExportFormatEnum.XLSX ? ExportType.XLSX : ExportType.CSV;
  exportData.exportType = selectedExportFormatFromDialog || selectedExportFormatFromSettings;

  return exportData;
};

export const getPortalGridColumns = async (gridId: string): Promise<PersistedGridColumn[]> => {
  const response = await axiosInstance.get<PortalGridConfigResponse>(endpoints.gridConfig, {
    baseURL: endpoints.PortalApiUrl,
    params: { gridId },
  });

  const parsedGridConfig = JSON.parse(response.data?.gridConfig || '[]');
  return Array.isArray(parsedGridConfig) ? parsedGridConfig : [];
};

export const handleDocumentGenerationError = (
  error: any,
  snackbar: (message: string, variant: SnackbarVariant) => void,
  t: (key: string, options?: any) => string,
  fallbackMessageKey = 'payments.generateReport.DOCUMENT_EXPORT_REQUEST_FAILED'
): void => {
  const status = error.response?.data?.status;
  let messageParams = {};
  let variant = 'error' as SnackbarVariant;

  if (status === 'LIMIT_EXCEEDED') {
    variant = 'warning';
    messageParams = {
      fileType: error.response?.data?.fileType,
      documentLimit: error.response?.data?.documentLimit,
    };
  } else if (status === 'NO_SESSION') {
    messageParams = { userSessionLimit: error.response?.data?.userSessionLimit };
  }

  const message = t(status ? `payments.generateReport.DOCUMENT_REQUEST_${status}` : fallbackMessageKey, messageParams);
  snackbar(message, variant);
};

export const generateExport = async (
  endpoint: string,
  fileBody: DataExport,
  sortModel: string[],
  filter: FiltersParams | undefined,
  snackbar: (message: string, variant: SnackbarVariant) => void,
  t: (key: string) => string
): Promise<void> => {
  const queryParams: string = [
    `csvbody=${encodeURIComponent(JSON.stringify(fileBody))}`,
    filter && `filter=${encodeURIComponent(JSON.stringify(filter))}`,
    sortModel?.length > 0 && `sort=${sortModel[0]}`,
  ]
    .filter(Boolean)
    .join('&');

  const url = `${endpoints.EFTApiUrl}/${endpoint}/${endpoints.export}?${queryParams}`;

  try {
    await axiosInstance.get(url);
    // Show snackbar notification instead of downloading the file
    snackbar(t('payments.generateReport.DOCUMENT_REQUEST_PENDING'), 'info');
  } catch (error) {
    handleDocumentGenerationError(error, snackbar, t);
  }
};

export const executeExport = (
  selectedNodes: IRowNode[],
  isAllSelected: boolean,
  selectedExportFormatFromDialog: ExportType,
  columnApi: ColumnApi,
  gridApi: GridApi,
  exportSettings: ExportSettings,
  endpoint: string,
  snackbar: (message: string, variant: SnackbarVariant) => void,
  t: (key: string) => string,
  columns?: ColumnDefExport[],
  extraFilterParams?: object,
  getRowIdPath?: GetRowNodeIdFunc
): void => {
  const columnsToExport = (columns || columnApi.getDisplayedCenterColumns())
    .map((column) => column.getColDef())
    .filter((colDef: ColDef) => !!colDef.headerName)
    .map((colDef: ColDef) => ({
      headerName: colDef.headerName,
      colIdExport: (colDef as any).colIdExport || colDef.colId,
    }));

  const sortedColumn: ColumnState[] = columnApi.getColumnState().filter((col: ColumnState) => col.sort !== null);
  const sortModel: string[] = mapSortModel(columnApi.getDisplayedCenterColumns(), sortedColumn);
  const dataExport: DataExport = {
    headers: columnsToExport.map((col: ColumnDefExport) => col.headerName),
    fields: columnsToExport.map((col: ColumnDefExport) => col.colIdExport),
    ...(!isAllSelected && {
      ids: selectedNodes?.map((row: IRowNode) => (getRowIdPath ? getRowIdPath(row) : row.data.uniqueId)),
    }),
  };
  const fileBody: DataExport = getFileBody(dataExport, exportSettings, selectedExportFormatFromDialog);
  const filterModel: { [p: string]: any } = gridApi.getFilterModel();
  const filterParams: FiltersParams = isAllSelected
    ? { ...mapFilterModel(filterModel, gridApi.getColumnDefs()), ...extraFilterParams }
    : null;

  generateExport(endpoint, fileBody, sortModel, filterParams, snackbar, t);
};

export const downloadClientSideData = async <T>(
  endpoint: string,
  fileBody: DataExport,
  rows: T[],
  snackbar: (message: string, variant: SnackbarVariant) => void,
  t: (key: string) => string,
  exportEndpoint: string = endpoints.exportClientSide,
  extraParams?: Params
): Promise<void> => {
  const url = `${endpoints.EFTApiUrl}/${endpoint}/${exportEndpoint}`;
  const params: Params = {
    csvbody: JSON.stringify(fileBody),
  };

  if (extraParams) {
    Object.assign(params, extraParams);
  }

  try {
    await axiosInstance.post(url, rows, {
      params,
    });
    // Show snackbar notification instead of downloading the file
    snackbar(t('payments.generateReport.DOCUMENT_REQUEST_PENDING'), 'info');
  } catch (error) {
    handleDocumentGenerationError(error, snackbar, t);
  }
};

export const generateReport = async (
  uniqueId: CreditTransferOrder['uniqueId'],
  sortBy: string,
  reportType: CreditTransferReportType,
  reportSubType: CreditTransferReportType,
  data: JsonObject,
  snackbar: (message: string, variant: SnackbarVariant) => void,
  t: (key: string) => string
): Promise<void> => {
  const { txGroupId, txId, ...reportData } = data;
  const params: Params = {
    reportType,
  };
  if (sortBy) params.sortBy = sortBy;
  if (reportSubType) params.reportSubType = reportSubType;
  if (txGroupId) params.txGroupId = txGroupId as string;
  if (txId) params.txId = txId as string;

  const url = `${endpoints.EFTApiUrl}/${endpoints.orders}/${uniqueId}/${endpoints.generateReport}`;
  try {
    await axiosInstance.put(url, reportData, {
      params,
    });
    // Show snackbar notification instead of downloading the file
    snackbar(t('payments.generateReport.DOCUMENT_REQUEST_PENDING'), 'info');
  } catch (error) {
    handleDocumentGenerationError(error, snackbar, t, 'payments.generateReport.DOCUMENT_REQUEST_FAILED');
  }
};
